/**
 * ThreeDsModal — 3DS bank verification challenge in a modal WebView.
 *
 * Intercepts the redirect to /booking/payment-complete to detect
 * approval status. Auto-cancels after 5 minutes.
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { View, Text, ActivityIndicator, Platform } from 'react-native'
import { AppModal } from '@/components/ui/AppModal'
import { useTranslation } from 'react-i18next'
import { X } from '@/components/ui/icons'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING } from '@/lib/theme/tokens'
import { FONTS, COLORS } from '@/lib/brand'
import { TrackedNativePressable as RNPressable } from '@/components/ui/TrackedNativePressable'
import {
  CHALLENGE_FRAME_ORIGINS,
  isAllowedChallengeHostname,
} from '@/lib/payments/adapters/challenge-origins'
import {
  isAllowedThreeDsMessageOrigin,
  isLocalCheckoutHost,
  isThreeDsCheckoutReturnUrl,
  isThreeDsReturnHost,
  parseThreeDsCompleteMessage,
} from '@/lib/payments/three-ds-complete'

// P0-4: react-native-webview has no web implementation — lazy import for native only
let WebView: typeof import('react-native-webview').WebView | undefined
if (Platform.OS !== 'web') {
  try {
    const rnw = require('react-native-webview')
    WebView = rnw.WebView ?? rnw.default
  } catch {
    // Not available — web platform
  }
}
type WebViewNavigation = import('react-native-webview').WebViewNavigation

const ALLOWED_ORIGINS = [...CHALLENGE_FRAME_ORIGINS]
const TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

/**
 * How long the "loading" veil may hide the bank page before we drop it.
 *
 * `loading` is cleared ONLY by the WebView's `onLoadEnd` / the iframe's
 * `onLoad`, and `startInLoadingState={false}` means there is no other path. A
 * bank page that hangs — or fails TLS in a way that fires neither callback —
 * left an opaque full-bleed veil up for the full five minutes of TIMEOUT_MS,
 * mid-charge. Dropping the veil does not cancel anything: it reveals the
 * WebView and the header underneath, so the guest can see what is happening
 * and decide. The 5-minute auto-cancel still backstops it.
 */
const LOAD_VEIL_TIMEOUT_MS = 20 * 1000

/**
 * The veil covers the page. The header must outrank it, always.
 *
 * Named rather than inline so the relationship is stated once and can be
 * checked — `scripts/validate-input-liveness.mjs` asserts
 * HEADER_Z_INDEX > VEIL_Z_INDEX. Two bare numbers 60 lines apart is how the
 * veil came to cover `three-ds.close` in the first place.
 */
const VEIL_Z_INDEX = 10
const HEADER_Z_INDEX = 20

interface ThreeDsModalProps {
  visible: boolean
  challengeUrl: string
  onComplete: () => void
  onCancel: () => void
}

export function ThreeDsModal({ visible, challengeUrl, onComplete, onCancel }: ThreeDsModalProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [loading, setLoading] = useState(true)
  const completedRef = useRef(false)

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setLoading(true)
      completedRef.current = false
    }
  }, [visible])

  const finishChallenge = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    onComplete()
  }, [onComplete])

  // Web 3DS stays in-app through an iframe. Cross-origin redirects cannot be
  // inspected by React, so this button only triggers backend verification.
  const handleWebDone = useCallback(() => {
    finishChallenge()
  }, [finishChallenge])

  // 5-minute timeout
  useEffect(() => {
    if (!visible) return

    const timer = setTimeout(() => {
      if (!completedRef.current) {
        onCancel()
      }
    }, TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [visible, onCancel])

  // Veil backstop — see LOAD_VEIL_TIMEOUT_MS. Purely visual: it uncovers the
  // page and the header, it never cancels the challenge.
  useEffect(() => {
    if (!visible || !loading) return
    const timer = setTimeout(() => setLoading(false), LOAD_VEIL_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [visible, loading])

  const isAllowedNavigationUrl = useCallback((rawUrl: string): boolean => {
    let url: URL
    try {
      url = new URL(rawUrl)
    } catch {
      return false
    }

    if (url.protocol === 'about:') return true
    const hostname = url.hostname.toLowerCase()
    if (isLocalCheckoutHost(hostname) && url.protocol === 'http:') return true
    if (url.protocol !== 'https:') return false
    if (isThreeDsReturnHost(hostname)) return true
    return isAllowedChallengeHostname(hostname)
  }, [])

  const handleNavigationChange = useCallback(
    (event: WebViewNavigation) => {
      if (!isAllowedNavigationUrl(event.url)) return false

      if (isThreeDsCheckoutReturnUrl(event.url)) {
        if (completedRef.current) return false
        finishChallenge()
        return false
      }
      return true
    },
    [finishChallenge, isAllowedNavigationUrl],
  )

  // Web iframe redirects are cross-origin (processor → fixtures 3ds-return).
  // The return page posts this message; without it the guest has to guess
  // that "Listo" is the only way to confirm the charge.
  useEffect(() => {
    if (!visible || Platform.OS !== 'web' || typeof window === 'undefined') return

    const onMessage = (event: MessageEvent) => {
      if (!isAllowedThreeDsMessageOrigin(event.origin)) return
      if (!parseThreeDsCompleteMessage(event.data)) return
      finishChallenge()
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [finishChallenge, visible])

  // Alert.alert is `static alert() {}` on react-native-web — an empty method.
  // This confirm lived inside a payment challenge, so on app.tago.rent the X
  // and the hardware back button both did nothing at all: the user was sealed
  // inside a 3DS modal mid-charge with no way out.
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false)
  const handleCancel = useCallback(() => setCancelConfirmVisible(true), [])

  return (
    <AppModal
      visible={visible}
      transparent={Platform.OS !== 'ios'}
      animationType={Platform.OS === 'android' ? 'fade' : 'slide'}
      presentationStyle={Platform.OS === 'ios' ? 'pageSheet' : undefined}
      onRequestClose={handleCancel}
      // The bank's page is a WebView (native) or an iframe (web). Both consume
      // their own touches, so the guest typing a one-time code into the bank's
      // form would look like a dead screen to the probe — on the payment sheet,
      // which is the last place that should generate a false signal.
    >
      <View
        style={{
          flex: 1,
          backgroundColor: colors.background,
        }}
      >
        {/*
          Header.

          `zIndex` here is load-bearing, not cosmetic. The loading veil below is
          absolutely positioned at inset 0 with `zIndex: 10`, and it is rendered
          AFTER this header — so without an explicit z-index it painted over the
          `three-ds.close` X and the web `three-ds.done` button. On Android the
          hardware back still reached `onRequestClose`, but iOS never fires
          `onRequestClose` (RN only wires it when `allowSwipeDismissal` is set,
          which nothing in this app sets) and mobile web has no back-to-close and
          no Escape key. On those two surfaces the covered X was the ONLY control,
          so a bank page that never finished loading sealed the guest inside a
          payment sheet. The controls must outrank the veil.

          Works on all four surfaces: react-native-web gives every View
          `position: relative`, so `zIndex` applies to this header as a CSS
          stacking context; on native, `zIndex` orders siblings directly.
        */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingTop: Platform.OS === 'ios' ? SPACING['2xl'] + SPACING.lg : SPACING.lg,
            paddingBottom: SPACING.sm,
            paddingHorizontal: SPACING.md,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
            backgroundColor: colors.background,
            zIndex: HEADER_Z_INDEX,
          }}
        >
          <RNPressable
            onPress={handleCancel}
            accessibilityRole="button"
            accessibilityLabel={t('common.close')}
            testID="three-ds.close"
            style={{
              width: 44,
              height: 44,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={20} color={colors.textSecondary} strokeWidth={2} />
          </RNPressable>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text
              style={{
                fontSize: 16,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
              numberOfLines={1}
            >
              {t('payment.bank_verification')}
            </Text>
          </View>
          {/* P0-4: On web, show "Done" button since we can't detect iframe redirect */}
          {Platform.OS === 'web' ? (
            <RNPressable
              onPress={handleWebDone}
              accessibilityRole="button"
              accessibilityLabel={t('common.done')}
              testID="three-ds.done"
              style={{
                width: 44,
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text style={{ fontSize: 14, fontFamily: FONTS.bodyBold, color: colors.textAccent }}>
                {t('common.done')}
              </Text>
            </RNPressable>
          ) : (
            <View style={{ width: 44 }} />
          )}
        </View>

        {/* Loading overlay */}
        {loading && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: VEIL_Z_INDEX,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.background,
            }}
          >
            <ActivityIndicator size="large" color={COLORS.coral.DEFAULT} />
            <Text
              style={{
                fontSize: 15,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                marginTop: SPACING.md,
              }}
            >
              {t('payment.loading_verification')}
            </Text>
          </View>
        )}

        {/* 3DS WebView — native uses react-native-webview, web uses iframe */}
        {challengeUrl ? (
          Platform.OS === 'web' ? (
            <iframe
              src={challengeUrl}
              onLoad={() => setLoading(false)}
              style={
                {
                  flex: 1,
                  border: 'none',
                  width: '100%',
                  height: '100%',
                  opacity: loading ? 0 : 1,
                } as React.CSSProperties
              }
              data-testid="three-ds.webview"
            />
          ) : WebView ? (
            <WebView
              source={{ uri: challengeUrl }}
              onLoadEnd={() => setLoading(false)}
              onShouldStartLoadWithRequest={(event) =>
                handleNavigationChange(event as WebViewNavigation)
              }
              onNavigationStateChange={handleNavigationChange}
              originWhitelist={ALLOWED_ORIGINS}
              incognito
              style={{ flex: 1, opacity: loading ? 0 : 1 }}
              javaScriptEnabled
              domStorageEnabled
              startInLoadingState={false}
              scalesPageToFit={Platform.OS === 'android'}
              testID="three-ds.webview"
            />
          ) : null
        ) : null}
      </View>
      <ConfirmDialog
        visible={cancelConfirmVisible}
        title={t('payment.cancel_verification_title')}
        message={t('payment.cancel_verification_desc')}
        confirmLabel={t('common.yes')}
        cancelLabel={t('common.no')}
        onConfirm={() => {
          setCancelConfirmVisible(false)
          onCancel()
        }}
        onCancel={() => setCancelConfirmVisible(false)}
        testIDPrefix="three-ds.cancel"
      />
    </AppModal>
  )
}
